import React from 'react'
import { Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import FormDateInput from '../atoms/FormDateInput'
import FormSelect from '../atoms/FormSelect'
import { FormFieldError, FormFieldLabel, FormTextInput } from '../atoms/FormTextInput'
import InsetSegmentedControl from '../atoms/InsetSegmentedControl'
import SellContractSegmentField, {
  sellContractFieldClass,
  SellContractFormControls,
  ShowSellContractError
} from './SellContractSegmentField'
import { SellContractFormData } from 'src/helpers/sellContractFormValidation'

interface SellContractDeliverySectionProps {
  values: SellContractFormData
  form: SellContractFormControls
  showError: ShowSellContractError
}

const SellContractDeliverySection = ({ values, form, showError }: SellContractDeliverySectionProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <>
      <div className="text-zinc-900 text-xl font-bold font-Montserrat leading-7">{t('sell_contract.delivery')}</div>
      <div className="self-stretch flex flex-col justify-start items-start gap-6">
        <Field name="delivery_time_span">
          {({ input }) => (
            <div className={sellContractFieldClass}>
              <FormFieldLabel label={t('sell_contract.date_format')} />
              <InsetSegmentedControl
                className="w-56"
                value={input.value ?? 'exact_date'}
                onChange={input.onChange}
                options={[
                  { value: 'exact_date', label: t('sell_contract.exact_date') },
                  { value: 'time_span', label: t('sell_contract.time_span') }
                ]}
              />
            </div>
          )}
        </Field>
        <Field name="delivery_date">
          {({ input, meta }) => (
            <div className={sellContractFieldClass}>
              <FormFieldLabel label={t('sell_contract.delivery_date')} />
              <FormDateInput
                value={input.value ?? ''}
                onChange={input.onChange}
                onBlur={input.onBlur}
                placeholder={t('sell_contract.date_placeholder')}
                error={showError(meta)}
              />
              <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
            </div>
          )}
        </Field>
        {values.delivery_time_span === 'time_span' ? (
          <Field name="delivery_period">
            {({ input, meta }) => (
              <div className={sellContractFieldClass}>
                <FormFieldLabel label={t('sell_contract.delivery_period')} />
                <FormSelect
                  value={input.value ?? ''}
                  onChange={input.onChange}
                  options={[
                    { value: 'one_month', label: t('sell_contract.month_1') },
                    { value: 'two_months', label: t('sell_contract.month_2') },
                    { value: 'three_months', label: t('sell_contract.month_3') },
                    { value: 'four_months', label: t('sell_contract.month_4') },
                    { value: 'five_months', label: t('sell_contract.month_5') }
                  ]}
                  placeholder={t('sell_contract.select')}
                  error={showError(meta)}
                />
                <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
              </div>
            )}
          </Field>
        ) : null}
        <SellContractSegmentField
          name="contract_binding_type"
          label={t('sell_contract.commitment')}
          values={values}
          form={form}
          showError={showError}
          fallback="binding"
          options={[
            { value: 'binding', label: t('sell_contract.binding') },
            { value: 'non_binding', label: t('sell_contract.non_binding') }
          ]}
        />
        <SellContractSegmentField
          name="delivery_type"
          label={t('sell_contract.delivery_method')}
          values={values}
          form={form}
          showError={showError}
          fallback="client"
          options={[
            { value: 'client', label: t('sell_contract.client_delivery') },
            { value: 'dealer', label: t('sell_contract.dealer_delivery') }
          ]}
        />
        <Field name="delivery_address">
          {({ input }) => (
            <div className={sellContractFieldClass}>
              <FormFieldLabel label={t('sell_contract.delivery_place')} />
              <FormTextInput value={input.value ?? ''} onChange={input.onChange} placeholder="" />
            </div>
          )}
        </Field>
        {values.client_type === 'company' ? null : (
          <>
            <SellContractSegmentField
              name="registration_state"
              label={t('sell_contract.registration_state')}
              values={values}
              form={form}
              showError={showError}
              className="w-full"
              required
              options={[
                { value: 'deregistered', label: t('sell_contract.registration_deregistered') },
                { value: 'registered', label: t('sell_contract.registration_registered') }
              ]}
            />
            <SellContractSegmentField
              name="registration_by"
              label={t('sell_contract.registration_by')}
              values={values}
              form={form}
              showError={showError}
              required
              options={[
                { value: 'seller', label: t('sell_contract.registration_seller') },
                { value: 'buyer', label: t('sell_contract.registration_buyer') }
              ]}
            />
          </>
        )}
      </div>
    </>
  )
}

export default SellContractDeliverySection
